import { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import AskAI from "./AskAI";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const [email, setEmail] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [loading, setLoading] = useState(true);

  // ADD (near your other useState hooks)
  const [billingLoading, setBillingLoading] = useState(false);

  // Ref to call AskAI's submit from here
  const runAskRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) {
        console.error("supabase.auth.getUser error:", error);
        setLoading(false);
        return;
      }
      const userEmail = data?.user?.email || null;
      setEmail(userEmail);

      if (userEmail) {
        try {
          const res = await fetch(
            `${BACKEND_URL}/check-status/${encodeURIComponent(userEmail)}`
          );
          const json = await res.json();
          setIsPaid(Boolean(json.paid));
        } catch (e) {
          console.error("check-status failed:", e);
        }
      }
      setLoading(false);
    })();
  }, []);

  const handleSubscribe = async () => {
    if (!email) return;
    try {
      const res = await fetch(`${BACKEND_URL}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const json = await res.json();
      if (res.ok && json.checkout_url) {
        window.location.href = json.checkout_url;
      } else {
        alert(json.detail || "Could not start checkout.");
      }
    } catch (e) {
      console.error(e);
      alert("Subscription error. Please try again.");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  const openBillingPortal = async () => {
    if (!email) return;
    try {
      setBillingLoading(true);
      const res = await fetch(`${BACKEND_URL}/billing/portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }), // uses your existing `email` state
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Failed to open billing portal");
      }
      const data = await res.json();
      window.location.href = data.url; // hand off to Stripe
    } catch (e) {
      alert(e.message || "Could not open billing portal.");
    } finally {
      setBillingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="content" style={{ minHeight: "100vh" }}>
        {/* Full-width topbar placeholder */}
        <div className="topbar" style={{ marginBottom: 18 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>Dashboard</div>
            <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
          </div>
          <div className="btn is-disabled">Logout</div>
        </div>

        <div className="card" style={{ maxWidth: 960, margin: "0 auto" }}>
          <div className="skeleton" style={{ height: 18, width: 160 }} />
          <div className="skeleton" style={{ height: 36, width: 220, marginTop: 12 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="content" style={{ minHeight: "100vh" }}>
      {/* FULL-WIDTH TOPBAR */}
      <div className="topbar" style={{ marginBottom: 18 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 20 }}>Dashboard</div>
          <div className="muted" style={{ fontSize: 14 }}>{email}</div>
        </div>
        <button onClick={handleLogout} className="btn">Logout</button>
      </div>

      {/* MAIN CONTENT AREA */}
      <div style={{ display: "grid", gap: 12, maxWidth: 960, margin: "0 auto" }}>
        {/* Paywall banner OR AI interface */}
        {isPaid ? (
          <div className="card" style={{ padding: 0 }}>
            {/* AskAI with external trigger and hidden small ask button */}
            <AskAI
              email={email}
              exposeRunRef={runAskRef}
              hideAskButton
            />
          </div>
        ) : (
          <div className="card">
            <p className="muted" style={{ margin: 0 }}>
              You’re on the free plan. Upgrade to unlock full features (file upload + AI Q&A + chart suggestions).
            </p>
            <button onClick={handleSubscribe} className="btn btn-primary" style={{ marginTop: 10 }}>
              Subscribe
            </button>
          </div>
        )}

        {isPaid && (
          <button
            onClick={openBillingPortal}
            disabled={billingLoading}
            aria-label="Manage billing"
            title="Manage billing (cancel or update in Stripe)"
            style={{
              position: "fixed",
              right: 12,
              bottom: 12,
              padding: "8px 10px",
              borderRadius: 999,
              background: "rgba(0,0,0,0.08)",    // subtle gray, not red
              color: "rgba(0,0,0,0.55)",
              fontSize: 12,
              border: "1px solid rgba(0,0,0,0.12)",
              boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              opacity: 0.35,                      // very faint by default
              transition: "opacity 150ms ease, transform 150ms ease",
              zIndex: 40,
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.85";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0.35";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {billingLoading ? "Opening…" : "Manage billing"}
          </button>
        )}

        {/* Run Analysis now triggers AskAI's same submit */}
        <button
          disabled={!isPaid}
          className={`btn ${isPaid ? "btn-primary" : "is-disabled"}`}
          onClick={() => {
            if (isPaid && runAskRef.current) runAskRef.current();
          }}
          style={{ padding: "12px 16px", borderRadius: 8 }}
        >
          Run Analysis
        </button>
      </div>
    </div>
  );
}